import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import { useContext } from "react";
import { useQuery } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { Alert, AlertTitle, Box } from "@mui/material";
import { SettingsContext } from "@/providers/SettingsContextProvider";
import { useSealReadRole } from "@/hooks/useSealReadRole";
import { isSealAtClose } from "@/services/ballotBoxSealPolicy";
import { GET_FAILED_BALLOT_BOX_SEALS } from "@/queries/GetBallotBoxSeals";
import { sealText } from "@/services/ballotBoxSealErrors";
/**
 * The failed seals of an election event (VOTE-FREEZE, D6): an incident the
 * event's Dashboard and Publish tab show until it is resolved, so it isn't
 * seen only on the election's own Dashboard or in the Logs. Each line names
 * the election, the area and the reason. Nothing is shown when no seal
 * failed, when the event doesn't seal at close, or when the viewer can't
 * read seals.
 */
export var FailedSealsBanner = function (_a) {
    var _b;
    var electionEventId = _a.electionEventId, presentation = _a.presentation;
    var t = useTranslation().t;
    var globalSettings = useContext(SettingsContext).globalSettings;
    var role = useSealReadRole();
    var data = useQuery(GET_FAILED_BALLOT_BOX_SEALS, {
        variables: { electionEventId: electionEventId !== null && electionEventId !== void 0 ? electionEventId : "" },
        skip: !electionEventId || !role || !isSealAtClose(presentation),
        pollInterval: globalSettings.QUERY_POLL_INTERVAL_MS,
        context: role ? { headers: { "x-hasura-role": role } } : undefined,
    }).data;
    var failed = (_b = data === null || data === void 0 ? void 0 : data.sequent_backend_ballot_box_seal) !== null && _b !== void 0 ? _b : [];
    if (!failed.length)
        return null;
    return (_jsxs(Alert, { severity: "error", sx: { mb: 2 }, className: "failed-seals-banner", children: [_jsx(AlertTitle, { children: t("dashboard.ballotBoxes.incident.title", { count: failed.length }) }), t("dashboard.ballotBoxes.incident.body"), _jsx(Box, { component: "ul", sx: { m: 0, mt: 1, pl: 2 }, children: failed.map(function (seal) {
                    var _a, _b, _c, _d;
                    return (_jsx("li", { children: t("dashboard.ballotBoxes.incident.line", {
                            election: (_a = seal.election_name) !== null && _a !== void 0 ? _a : seal.election_id,
                            area: (_d = (_b = seal.area_name) !== null && _b !== void 0 ? _b : (_c = seal.area) === null || _c === void 0 ? void 0 : _c.name) !== null && _d !== void 0 ? _d : seal.area_id,
                            reason: sealText(t, seal.failure_reason),
                        }) }, seal.id));
                }) })] }));
};
